import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTestimonials } from '../hooks/useWebsiteContent';
import { Star, Instagram, Facebook, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const TestimonialsSection = () => {
  const { language } = useLanguage();
  const { testimonials, loading } = useTestimonials();

  const content = {
    en: {
      title: "Client Testimonials",
      subtitle: "What Our Customers Say"
    },
    ar: {
      title: "آراء العملاء",
      subtitle: "آراء طلابنا"
    }
  };

  const t = content[language];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-teal-600 font-semibold text-sm uppercase tracking-wide">
            {t.title}
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mt-2 mb-4">{t.subtitle}</h2>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {loading ? (
            <div className="col-span-3 flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            </div>
          ) : (
          testimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-gradient-to-br from-slate-50 to-teal-50/30 rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              {/* Rating Stars */}
              <div className="flex space-x-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>

              {/* Testimonial Text */}
              <p className="text-gray-700 leading-relaxed mb-6 text-sm">
                "{testimonial.text[language]}"
              </p>

              {/* Customer Info */}
              <div className="flex items-center space-x-4 pt-4 border-t border-gray-200">
                <img
                  src={testimonial.image}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-teal-500"
                />
                <div>
                  <h4 className="font-bold text-gray-900">{testimonial.name}</h4>
                  <p className="text-sm text-gray-500">
                    {language === 'en' ? 'Verified Student' : 'طالب موثق'}
                  </p>
                </div>
              </div>
            </div>
          ))
          )}
        </div>

        {/* Social Proof Section - Enhanced */}
        <div className="mt-20">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">
              {language === 'en' ? 'More Reviews on Social Media' : 'المزيد من التقييمات على وسائل التواصل'}
            </h3>
            <p className="text-gray-600">
              {language === 'en' ? 'Join thousands of satisfied students!' : 'انضم إلى آلاف الطلاب الراضين!'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* Instagram Reviews */}
            <a
              href="https://www.instagram.com/p/C7GByAVvAJ8/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA=="
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-2xl p-8 text-white hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              <div className="relative z-10 flex flex-col items-center text-center space-y-4">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Instagram className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h4 className="text-2xl font-bold mb-2">Instagram</h4>
                  <p className="text-white/90 mb-4">
                    {language === 'en' ? 'See real student success stories' : 'شاهد قصص نجاح الطلاب الحقيقية'}
                  </p>
                  <div className="flex items-center justify-center space-x-2 text-sm font-semibold">
                    <Star className="w-5 h-5 fill-white" />
                    <span>5.0</span>
                    <span className="text-white/80">• 1000+ {language === 'en' ? 'Reviews' : 'تقييم'}</span>
                  </div>
                </div>
                <Button 
                  className="bg-white text-purple-600 hover:bg-white/90 font-bold px-6 py-3 rounded-xl group-hover:shadow-lg transition-all"
                >
                  {language === 'en' ? 'View Instagram Reviews' : 'عرض تقييمات إنستغرام'}
                </Button>
              </div>
              
              {/* Decorative background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-white rounded-full translate-y-20 -translate-x-20"></div>
              </div>
            </a>

            {/* Facebook Reviews */}
            <a
              href="https://www.facebook.com/story.php?story_fbid=432189472896028&id=100083149366467&mibextid=wwXIfr&rdid=EgliEmFZplZkbzCa#"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-8 text-white hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              <div className="relative z-10 flex flex-col items-center text-center space-y-4">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <Facebook className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h4 className="text-2xl font-bold mb-2">Facebook</h4>
                  <p className="text-white/90 mb-4">
                    {language === 'en' ? 'Read testimonials from our community' : 'اقرأ شهادات مجتمعنا'}
                  </p>
                  <div className="flex items-center justify-center space-x-2 text-sm font-semibold">
                    <Star className="w-5 h-5 fill-white" />
                    <span>4.9</span>
                    <span className="text-white/80">• 800+ {language === 'en' ? 'Reviews' : 'تقييم'}</span>
                  </div>
                </div>
                <Button 
                  className="bg-white text-blue-600 hover:bg-white/90 font-bold px-6 py-3 rounded-xl group-hover:shadow-lg transition-all"
                >
                  {language === 'en' ? 'View Facebook Reviews' : 'عرض تقييمات فيسبوك'}
                </Button>
              </div>
              
              {/* Decorative background pattern */}
              <div className="absolute inset-0 opacity-10">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-40 h-40 bg-white rounded-full translate-y-20 -translate-x-20"></div>
              </div>
            </a>
          </div>

          {/* Trust Badge */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center space-x-3 bg-white rounded-full px-8 py-4 shadow-lg">
              <div className="flex -space-x-2">
                {testimonials.slice(0, 3).map((testimonial, index) => (
                  <img
                    key={index}
                    src={testimonial.image}
                    alt=""
                    className="w-10 h-10 rounded-full border-2 border-white object-cover"
                  />
                ))}
              </div>
              <div className="text-left">
                <p className="font-bold text-gray-900">5,000+ {language === 'en' ? 'Happy Students' : 'طالب سعيد'}</p>
                <p className="text-sm text-gray-600">{language === 'en' ? 'Rated 4.9/5 on average' : 'تقييم 4.9/5 في المتوسط'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
